import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';

/**
 * Bare `integrationApi` — owns the reducerPath, shared base query, and
 * the central tag-type taxonomy. Per-resource files (`./plugins`,
 * `./merchantPlugins`, `./executionLogs`) call `integrationApi.injectEndpoints`
 * to attach their endpoints. RTK Query mutates the shared definition in
 * place at injection time, so `integrationApi.endpoints[name]` becomes
 * available once the injecting file is imported.
 *
 * The two small lookup endpoints (`getCarriers`, `getPluginTypes`) live
 * here as well — they don't fit any per-resource bucket and aren't
 * worth their own file. Keep this file under ~150 LOC; if it grows,
 * move the lookups into a `./lookups.ts` follow-up.
 */
export interface Carrier {
  id?: string;
  name: string;
  /** Other fields the integration service returns (e.g. logoUrl) are not used yet. */
  [key: string]: unknown;
}

/**
 * Plugin-type option used by the merchant settings `AllowedPluginTypes`
 * multi-select. The integration service ships rows as
 * `{ id, description }` (the `id` is the canonical identifier stored on
 * the merchant config; `description` is the display string). We normalise
 * those to `{ value, label }` so the multi-select can bind directly and
 * the rest of the codebase doesn't need to know about the underlying
 * field names.
 */
export interface PluginTypeOption {
  label: string;
  value: string;
  /** Other fields the integration service may return are unused today. */
  [key: string]: unknown;
}

/** Raw row shape as returned by `GET /integration/v1/pluginType`. */
interface RawPluginType {
  id?: string;
  description?: string;
  /** Older deployments shipped these field names; kept for safety. */
  label?: string;
  value?: string;
  name?: string;
  [key: string]: unknown;
}

export const integrationApi = createApi({
  reducerPath: 'integrationApi',
  baseQuery: portalBaseQuery,
  tagTypes: [
    'Carriers',
    'PluginTypes',
    'Plugins',
    'PluginBasicInfo',
    'PluginVersions',
    'Templates',
    'MerchantPlugins',
    'MerchantPluginDetail',
    'ExecutionLogs',
    'EventTypes',
    'Transformation',
    'InboundAuthConfig',
    'AccessRequests',
    'AccessRequestCounts',
    'VersionDistribution',
    'PluginConnections',
    'TaxCodeMappings',
  ],
  endpoints: (builder) => ({
    getCarriers: builder.query<Carrier[], void>({
      query: () => ({
        url: `${services.integration}/v1/carrier`,
        method: 'GET',
      }),
      transformResponse: (response: Carrier[] | { carriers?: Carrier[] }) =>
        Array.isArray(response) ? response : (response.carriers ?? []),
      providesTags: () => [{ type: 'Carriers', id: 'LIST' }],
    }),
    getPluginTypes: builder.query<PluginTypeOption[], void>({
      query: () => ({
        url: `${services.integration}/v1/pluginType`,
        method: 'GET',
      }),
      // The service has shipped both a bare array and a `{ pluginTypes: [...] }`
      // wrapper at different times. Normalise here so callers always get an
      // array. Items missing `value` (older payloads used `name`) are
      // backfilled from `label` so the picker still binds something.
      transformResponse: (
        response: RawPluginType[] | { pluginTypes?: RawPluginType[] },
      ): PluginTypeOption[] => {
        const list = Array.isArray(response) ? response : (response.pluginTypes ?? []);
        // The integration service ships rows as `{ id, description }`. We
        // normalise to `{ value, label }`: `value` is the canonical
        // identifier stored on the merchant config (must match the
        // `id`), `label` is the display string. Older payloads that
        // shipped `value`/`label`/`name` directly are still handled by
        // the fallback chain. Rows that don't yield any usable
        // identifier are dropped so the multi-select doesn't render an
        // empty/unidentifiable option.
        return list
          .map((p) => {
            const value = p.value ?? p.id ?? p.name ?? p.label;
            const label = p.label ?? p.description ?? p.name ?? value;
            if (!value) return null;
            return { ...p, value, label: label ?? value };
          })
          .filter((p): p is PluginTypeOption => p !== null);
      },
      providesTags: () => [{ type: 'PluginTypes', id: 'LIST' }],
    }),
  }),
});

export const { useGetCarriersQuery, useGetPluginTypesQuery } = integrationApi;

/**
 * Shared `unwrapArray` helper used by plugins + merchantPlugins endpoints.
 * The integration service occasionally returns a bare array and other
 * times a `{ plugins: [...] }` / `{ merchantPlugins: [...] }` / `{ data: [...] }`
 * envelope; this helper normalises all three.
 */
export function unwrapArray<T>(
  response: T[] | { plugins?: T[]; merchantPlugins?: T[]; data?: T[] },
): T[] {
  if (Array.isArray(response)) return response;
  const obj = response as { plugins?: T[]; merchantPlugins?: T[]; data?: T[] };
  return obj.plugins ?? obj.merchantPlugins ?? obj.data ?? [];
}
